import { useState, useCallback } from 'react';

/**
 * A React hook that mimics the behavior of Angular Material's SelectionModel with trackBy support.
 *
 * @template T The type of items in the selection.
 * @param {T[]} [initialSelection] Initial selected items.
 * @param {boolean} [multiple=true] Whether multiple items can be selected.
 * @param {(item: T) => any} [trackBy] Optional function to track items by a unique property.
 * @returns {object} An object containing selection management functions and state.
 */
const useSelectionModel = <T extends { [key: string]: any }>(
  initialSelection: T[] = [],
  multiple = true,
  trackBy?: (item: T) => any,
) => {
  const [selected, setSelected] = useState<T[]>(initialSelection);

  const itemEquals = useCallback(
    (item1: T, item2: T) => {
      if (trackBy) {
        return trackBy(item1) === trackBy(item2);
      }
      return item1 === item2; // Default: object reference comparison
    },
    [trackBy],
  );

  const findIndex = useCallback(
    (item: T, array: T[]) => {
      for (let i = 0; i < array.length; i++) {
        if (itemEquals(item, array[i])) {
          return i;
        }
      }
      return -1;
    },
    [itemEquals],
  );

  const includes = useCallback(
    (item: T, array: T[]) => findIndex(item, array) !== -1,
    [findIndex],
  );

  const select = useCallback(
    (...items: T[]) => {
      setSelected((prevSelected) => {
        if (multiple) {
          const newSelection = [...prevSelected];
          items.forEach((item) => {
            if (!includes(item, newSelection)) {
              newSelection.push(item);
            }
          });
          return newSelection;
        }
        return items.length > 0 ? [items[0]] : [];
      });
    },
    [multiple, includes],
  );

  const deselect = useCallback(
    (...items: T[]) => {
      setSelected((prevSelected) => prevSelected.filter((item) => !includes(item, items)));
    },
    [includes],
  );

  const toggle = useCallback(
    (...items: T[]) => {
      setSelected((prevSelected) => {
        const newSelection = [...prevSelected];
        items.forEach((item) => {
          const index = findIndex(item, newSelection);
          if (index !== -1) {
            newSelection.splice(index, 1);
          } else if (multiple) {
            newSelection.push(item);
          } else {
            newSelection.length = 0;
            newSelection.push(item);
          }
        });
        return newSelection;
      });
    },
    [multiple, findIndex],
  );

  const isSelected = useCallback((item: T) => includes(item, selected), [selected, includes]);
  const clear = useCallback(() => { setSelected([]); }, []);
  const setSelection = useCallback((items: T[]) => { setSelected(items); }, []);
  const getSelected = useCallback(() => selected, [selected]);
  const isEmpty = useCallback(() => selected.length === 0, [selected]);
  const hasValue = useCallback(() => selected.length > 0, [selected]);

  return {
    select,
    deselect,
    toggle,
    isSelected,
    clear,
    getSelected,
    isEmpty,
    hasValue,
    setSelection,
    selected,
  };
};

export default useSelectionModel;
